/* eslint-disable */
import { useState, useEffect } from "react";
import { AuthProvider, AuthScreen, UserPill, useAuth, saveToStorage, loadFromStorage, ToastProvider } from "./Auth";
import { syncSquadToCloud, performInitialSync } from "./SupabaseSync";
import PlayerDatabase from "./PlayerDatabase";
import CoachingKnowledge from "./CoachingKnowledge";
import SquadCoach from "./SquadCoach";
import TacticsBoard from "./TacticsBoard";
import VideoAnalysis from "./VideoAnalysis";
import Lab from "./Lab";
import ScoutReports from "./ScoutReports";
import Dashboard from "./Dashboard";
import OppositionDossier from "./OppositionDossier";
import SetPieceBuilder from "./SetPieceBuilder";
import PlayerComparison from "./PlayerComparison";
import AIAnalyst from "./AIAnalyst";
import Fixtures from "./Fixtures";
import Profile from "./Profile";
import SeasonTracker from "./SeasonTracker";
import LoadManagement from "./LoadManagement";

/* ═══════════════════════════════════════════════════════
   FOOTY ANALYST — footyanalyst.com
   Palette: Moonrise Kingdom (Wes Anderson)
   Font: Bebas Neue (headlines) + DM Sans (body)
   Theme: Editorial boldness meets Football Manager precision
═══════════════════════════════════════════════════════ */

const CSS = `
@import url('https://fonts.googleapis.com/css2?family=Bebas+Neue&family=DM+Sans:ital,opsz,wght@0,9..40,300;0,9..40,500;0,9..40,700;0,9..40,800;1,9..40,300;1,9..40,500&display=swap');

*, *::before, *::after { box-sizing: border-box; margin: 0; padding: 0; }

:root {
  /* ── Moonrise Kingdom palette ── */
  --teal:       #2e7d7a;
  --teal2:      #235f5c;
  --teal3:      #d4eceb;
  --teal4:      #eaf4f4;
  --sand:       #c4a882;
  --sand2:      #a8906a;
  --sand3:      #f5ede0;
  --sand4:      #faf5ee;
  --mustard:    #d4941a;
  --mustard2:   #b87d10;
  --mustard3:   #fdefd0;
  --darkred:    #8b2a2a;
  --darkred2:   #6e1f1f;
  --darkred3:   #f5d8d8;
  --cream:      #f7f2e8;
  --cream2:     #ede6d5;
  --ink:        #1a1612;
  --ink2:       #2d261e;
  --ink3:       #4a3f35;
  --muted:      #7a6e62;
  --white:      #fdfaf5;
  --rule:       rgba(26,22,18,0.1);
  --rule2:      rgba(26,22,18,0.05);
  --good:       #2e7d7a;
  --warn:       #d4941a;
  --bad:        #8b2a2a;
}

body {
  background: var(--cream);
  color: var(--ink);
  font-family: 'DM Sans', sans-serif;
  overflow-x: hidden;
}

/* Paper grain overlay */
body::after {
  content: '';
  position: fixed; inset: 0;
  background-image: url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='300' height='300'%3E%3Cfilter id='n'%3E%3CfeTurbulence type='fractalNoise' baseFrequency='0.7' numOctaves='4' stitchTiles='stitch'/%3E%3C/filter%3E%3Crect width='300' height='300' filter='url(%23n)' opacity='0.03'/%3E%3C/svg%3E");
  pointer-events: none; z-index: 9999;
}

/* ── NAV ── */
.nav {
  position: fixed; top: 0; left: 0; right: 0; z-index: 500;
  background: var(--teal);
  border-bottom: 3px solid var(--mustard);
  display: flex; align-items: center; justify-content: space-between;
  padding: 0 2.5rem; height: 58px;
}
.logo { display: flex; align-items: center; gap: 0.7rem; cursor: pointer; }
.logo-badge {
  width: 38px; height: 38px;
  background: var(--mustard);
  border: 2.5px solid var(--white);
  display: flex; align-items: center; justify-content: center;
  font-family: 'Bebas Neue', sans-serif;
  font-size: 1.1rem; letter-spacing: 1px; color: var(--ink);
  transform: rotate(-4deg);
  box-shadow: 2px 2px 0 rgba(0,0,0,0.2);
}
.logo-wordmark { display: flex; flex-direction: column; }
.logo-text {
  font-family: 'Bebas Neue', sans-serif;
  font-size: 1.5rem; letter-spacing: 3px; color: var(--white);
  line-height: 1;
}
.logo-sub {
  font-family: 'DM Sans', sans-serif;
  font-size: 0.48rem; color: var(--mustard); letter-spacing: 3px;
  text-transform: uppercase; font-weight: 500;
}
.nav-links { display: flex; }
.nav-link {
  font-family: 'DM Sans', sans-serif; font-size: 0.72rem; font-weight: 700;
  letter-spacing: 2px; text-transform: uppercase;
  padding: 0 1.1rem; height: 58px; display: flex; align-items: center;
  color: rgba(255,255,255,0.7); cursor: pointer; transition: all 0.2s;
  border-right: 1px solid rgba(255,255,255,0.1);
}
.nav-link:hover { color: var(--mustard); background: rgba(0,0,0,0.1); }
.nav-btns { display: flex; gap: 0.6rem; align-items: center; }

/* Live counter in nav */
.live-counter {
  display: flex; align-items: center; gap: 0.5rem;
  background: rgba(0,0,0,0.2); border: 1px solid rgba(255,255,255,0.15);
  padding: 0.3rem 0.8rem; border-radius: 2px;
}
.live-dot {
  width: 7px; height: 7px; border-radius: 50%; background: #4ade80;
  animation: pulse 2s infinite;
}
@keyframes pulse { 0%,100%{opacity:1;transform:scale(1)} 50%{opacity:0.6;transform:scale(0.85)} }
.live-text {
  font-family: 'DM Sans', sans-serif; font-size: 0.65rem; font-weight: 700;
  letter-spacing: 1.5px; text-transform: uppercase; color: var(--white);
}
.live-num {
  font-family: 'Bebas Neue', sans-serif; font-size: 1rem;
  letter-spacing: 1px; color: #4ade80;
}

.btn-nav-o {
  background: transparent; border: 1.5px solid rgba(255,255,255,0.4);
  color: var(--white); padding: 0.4rem 1rem;
  font-family: 'DM Sans', sans-serif; font-size: 0.7rem; font-weight: 700;
  letter-spacing: 2px; text-transform: uppercase; cursor: pointer; transition: all 0.2s;
}
.btn-nav-o:hover { border-color: var(--mustard); color: var(--mustard); }
.btn-nav-f {
  background: var(--mustard); border: 1.5px solid var(--mustard);
  color: var(--ink); padding: 0.4rem 1rem;
  font-family: 'DM Sans', sans-serif; font-size: 0.7rem; font-weight: 800;
  letter-spacing: 2px; text-transform: uppercase; cursor: pointer; transition: all 0.2s;
}
.btn-nav-f:hover { background: var(--mustard2); }

/* ── HERO ── */
.hero {
  min-height: 100vh; background: var(--teal);
  display: flex; flex-direction: column; align-items: center; justify-content: center;
  text-align: center; padding: 8rem 3rem 5rem; position: relative; overflow: hidden;
}
/* Moonrise Kingdom horizontal stripes */
.hero::before {
  content: '';
  position: absolute; inset: 0;
  background: repeating-linear-gradient(
    0deg, transparent, transparent 60px,
    rgba(0,0,0,0.06) 60px, rgba(0,0,0,0.06) 61px
  );
  pointer-events: none;
}
.hero-corner {
  position: absolute; width: 90px; height: 90px;
  border: 2px solid rgba(255,255,255,0.2);
}
.hero-corner.tl { top: 72px; left: 36px; }
.hero-corner.tr { top: 72px; right: 36px; }
.hero-corner.bl { bottom: 36px; left: 36px; }
.hero-corner.br { bottom: 36px; right: 36px; }

.hero-eyebrow {
  display: inline-flex; align-items: center; gap: 0.8rem;
  border: 1px solid rgba(255,255,255,0.3);
  padding: 0.4rem 1.4rem; margin-bottom: 1.8rem;
  position: relative; z-index: 1;
}
.hero-eyebrow-text {
  font-family: 'DM Sans', sans-serif; font-size: 0.65rem; font-weight: 700;
  letter-spacing: 4px; text-transform: uppercase; color: var(--mustard);
}
.hero-eyebrow-dot { width: 5px; height: 5px; background: var(--mustard); border-radius: 50%; }

.hero-h1 {
  font-family: 'Bebas Neue', sans-serif;
  font-size: clamp(5rem, 13vw, 13rem);
  letter-spacing: 4px; line-height: 0.88;
  color: var(--white); position: relative; z-index: 1;
}
.hero-h1 em { color: var(--mustard); font-style: normal; }

.hero-ornament {
  display: flex; align-items: center; gap: 1rem;
  justify-content: center; margin: 1.2rem 0 1.6rem;
  position: relative; z-index: 1;
}
.hero-orn-line { width: 80px; height: 1px; background: rgba(255,255,255,0.3); }
.hero-orn-sym { color: var(--mustard); font-size: 0.75rem; letter-spacing: 4px; }

.hero-sub {
  font-family: 'DM Sans', sans-serif; font-size: 1rem; font-weight: 300;
  color: rgba(255,255,255,0.8); max-width: 520px; line-height: 1.85;
  font-style: italic; margin-bottom: 2.5rem; position: relative; z-index: 1;
}
.hero-btns { display: flex; gap: 1rem; justify-content: center; flex-wrap: wrap; position: relative; z-index: 1; }
.btn-hero-p {
  background: var(--mustard); color: var(--ink);
  border: 2px solid var(--mustard); padding: 1rem 2.8rem;
  font-family: 'DM Sans', sans-serif; font-weight: 800; font-size: 0.85rem;
  letter-spacing: 2.5px; text-transform: uppercase; cursor: pointer; transition: all 0.22s;
}
.btn-hero-p:hover { background: transparent; color: var(--mustard); }
.btn-hero-s {
  background: transparent; color: var(--white);
  border: 2px solid rgba(255,255,255,0.5); padding: 1rem 2.8rem;
  font-family: 'DM Sans', sans-serif; font-weight: 700; font-size: 0.85rem;
  letter-spacing: 2.5px; text-transform: uppercase; cursor: pointer; transition: all 0.22s;
}
.btn-hero-s:hover { border-color: var(--white); }

/* LIVE USERS hero block */
.hero-live {
  display: flex; align-items: center; gap: 0.7rem;
  margin-top: 3rem; position: relative; z-index: 1;
  background: rgba(0,0,0,0.2); border: 1px solid rgba(255,255,255,0.15);
  padding: 0.7rem 1.4rem;
}
.hero-live-dot { width: 9px; height: 9px; border-radius: 50%; background: #4ade80; animation: pulse 2s infinite; }
.hero-live-text { font-family: 'DM Sans', sans-serif; font-size: 0.72rem; font-weight: 500; color: rgba(255,255,255,0.75); letter-spacing: 1px; }
.hero-live-num { font-family: 'Bebas Neue', sans-serif; font-size: 1.6rem; letter-spacing: 2px; color: #4ade80; line-height: 1; }

.hero-stats { display: flex; border: 1px solid rgba(255,255,255,0.18); margin-top: 1rem; position: relative; z-index: 1; }
.hs { padding: 1.4rem 2rem; border-right: 1px solid rgba(255,255,255,0.12); text-align: center; }
.hs:last-child { border-right: none; }
.hs-n { font-family: 'Bebas Neue', sans-serif; font-size: 2.6rem; letter-spacing: 2px; color: var(--mustard); line-height: 1; }
.hs-l { font-family: 'DM Sans', sans-serif; font-size: 0.6rem; font-weight: 500; color: rgba(255,255,255,0.55); letter-spacing: 3px; text-transform: uppercase; margin-top: 0.25rem; }

/* ── MARQUEE ── */
.mq { background: var(--darkred); border-top: 2px solid var(--darkred2); border-bottom: 2px solid var(--darkred2); padding: 0.55rem 0; overflow: hidden; white-space: nowrap; }
@keyframes mqs { from { transform: translateX(0); } to { transform: translateX(-50%); } }
.mq-inner { display: inline-flex; animation: mqs 28s linear infinite; }
.mq-item { font-family: 'Bebas Neue', sans-serif; font-size: 0.9rem; letter-spacing: 4px; color: var(--sand3); padding: 0 2.5rem; }
.mq-sep { color: var(--mustard); opacity: 0.7; }

/* ── SECTIONS ── */
.sec { padding: 6rem 3rem; }
.sec-teal   { background: var(--teal4);   border-top: 2px solid var(--rule); border-bottom: 2px solid var(--rule); }
.sec-sand   { background: var(--sand4);   border-top: 2px solid var(--rule); border-bottom: 2px solid var(--rule); }
.sec-mustard{ background: var(--mustard3);border-top: 2px solid var(--rule); border-bottom: 2px solid var(--rule); }
.sec-red    { background: var(--darkred3);border-top: 2px solid var(--rule); border-bottom: 2px solid var(--rule); }
.sec-label { display: flex; align-items: center; gap: 1rem; margin-bottom: 1rem; }
.sec-line { flex: 1; height: 2px; background: var(--rule); }
.sec-tag { font-family: 'DM Sans', sans-serif; font-size: 0.65rem; font-weight: 800; letter-spacing: 4px; text-transform: uppercase; color: var(--teal); }
.sec-h { font-family: 'Bebas Neue', sans-serif; font-size: clamp(3rem, 5.5vw, 5rem); letter-spacing: 3px; line-height: 0.95; margin-bottom: 1rem; color: var(--ink); }
.sec-h em { color: var(--darkred); font-style: normal; }
.sec-sub { font-family: 'DM Sans', sans-serif; font-size: 0.9rem; font-weight: 300; color: var(--muted); max-width: 460px; line-height: 1.8; font-style: italic; }

/* ── FEATURES ── */
.feat-grid { display: grid; grid-template-columns: repeat(3,1fr); gap: 1.2rem; margin-top: 3rem; }
.feat-card {
  background: var(--white); border: 2px solid var(--rule); padding: 1.8rem;
  position: relative; transition: all 0.22s;
}
.feat-card:nth-child(1) { border-top: 4px solid var(--teal); }
.feat-card:nth-child(2) { border-top: 4px solid var(--darkred); }
.feat-card:nth-child(3) { border-top: 4px solid var(--mustard); }
.feat-card:nth-child(4) { border-top: 4px solid var(--sand); }
.feat-card:nth-child(5) { border-top: 4px solid var(--teal); }
.feat-card:nth-child(6) { border-top: 4px solid var(--darkred); }
.feat-card:hover { transform: translateY(-3px); box-shadow: 4px 4px 0 var(--ink); }
.feat-num { font-family: 'Bebas Neue', sans-serif; font-size: 3rem; letter-spacing: 2px; color: rgba(26,22,18,0.06); line-height: 1; margin-bottom: 0.4rem; }
.feat-icon { font-size: 1.7rem; margin-bottom: 0.8rem; }
.feat-title { font-family: 'DM Sans', sans-serif; font-size: 1rem; font-weight: 800; margin-bottom: 0.55rem; letter-spacing: 0.5px; }
.feat-desc { font-family: 'DM Sans', sans-serif; font-size: 0.82rem; font-weight: 300; color: var(--muted); line-height: 1.75; font-style: italic; }

/* ── VIDEO TYPES ── */
.vt-grid { display: grid; grid-template-columns: repeat(2,1fr); gap: 1.1rem; margin-top: 2.5rem; }
.vt-card { background: var(--white); border: 2px solid var(--rule); padding: 1.4rem; transition: all 0.2s; }
.vt-card:nth-child(1) { background: var(--teal4); border-left: 4px solid var(--teal); }
.vt-card:nth-child(2) { background: var(--sand4); border-left: 4px solid var(--sand); }
.vt-card:nth-child(3) { background: var(--mustard3); border-left: 4px solid var(--mustard); }
.vt-card:nth-child(4) { background: var(--darkred3); border-left: 4px solid var(--darkred); }
.vt-card:hover { transform: translateY(-2px); box-shadow: 3px 3px 0 var(--ink); }
.vt-icon { font-size: 1.8rem; margin-bottom: 0.6rem; }
.vt-title { font-family: 'DM Sans', sans-serif; font-size: 0.95rem; font-weight: 800; margin-bottom: 0.4rem; }
.vt-desc { font-family: 'DM Sans', sans-serif; font-size: 0.78rem; font-weight: 300; color: var(--ink3); line-height: 1.72; font-style: italic; }
.vt-formats { display: flex; gap: 0.3rem; flex-wrap: wrap; margin-top: 0.7rem; }
.vt-fmt { font-family: 'DM Sans', sans-serif; font-size: 0.58rem; font-weight: 700; letter-spacing: 1.5px; text-transform: uppercase; padding: 0.12rem 0.45rem; border: 1.5px solid var(--ink); color: var(--ink); background: rgba(255,255,255,0.6); }

/* ── PRO STYLES LANDING ── */
.psg-grid { display: grid; grid-template-columns: repeat(2,1fr); gap: 1.2rem; margin-top: 2.5rem; }
.psg-card { background: var(--white); border: 2px solid var(--rule); padding: 1.6rem; position: relative; overflow: hidden; transition: all 0.22s; cursor: pointer; }
.psg-card:hover { box-shadow: 5px 5px 0 var(--ink); transform: translate(-2px,-2px); }
.psg-accent { position: absolute; top: 0; left: 0; right: 0; height: 5px; }
.psg-club { font-family: 'Bebas Neue', sans-serif; font-size: 1.5rem; letter-spacing: 2px; margin-top: 0.5rem; }
.psg-form { font-family: 'DM Sans', sans-serif; font-size: 0.65rem; letter-spacing: 3px; text-transform: uppercase; color: var(--muted); margin-top: 0.1rem; font-weight: 500; }
.psg-desc { font-family: 'DM Sans', sans-serif; font-size: 0.8rem; font-weight: 300; color: var(--ink3); line-height: 1.72; font-style: italic; margin: 0.9rem 0; padding-left: 0.85rem; border-left: 3px solid var(--teal); }
.psg-pl-label { font-family: 'DM Sans', sans-serif; font-size: 0.6rem; font-weight: 800; letter-spacing: 3px; text-transform: uppercase; color: var(--teal); margin-bottom: 0.5rem; }
.psg-pl-row { background: var(--cream2); border: 1px solid var(--rule); padding: 0.55rem 0.85rem; margin-bottom: 0.38rem; display: flex; align-items: center; gap: 0.7rem; }
.psg-pl-name { font-family: 'DM Sans', sans-serif; font-size: 0.82rem; font-weight: 700; flex: 1; }
.psg-pl-trait { font-family: 'DM Sans', sans-serif; font-size: 0.62rem; color: var(--teal2); font-style: italic; font-weight: 500; }
.psg-apply { width: 100%; margin-top: 1rem; padding: 0.55rem; font-family: 'DM Sans', sans-serif; font-size: 0.7rem; font-weight: 800; letter-spacing: 2px; text-transform: uppercase; background: transparent; border: 2px solid var(--ink); color: var(--ink); cursor: pointer; transition: all 0.2s; }
.psg-apply:hover { background: var(--ink); color: var(--white); }

/* ── PRICING ── */
.price-grid { display: grid; grid-template-columns: repeat(3,1fr); gap: 1.2rem; margin-top: 3rem; }
.price-card { background: var(--white); border: 2px solid var(--rule); padding: 2.2rem; position: relative; transition: transform 0.22s; }
.price-card:hover { transform: translateY(-4px); }
.price-card.hot { background: var(--teal4); border-color: var(--teal); }
.price-badge { position: absolute; top: -13px; left: 50%; transform: translateX(-50%); background: var(--darkred); color: var(--white); font-family: 'DM Sans', sans-serif; font-size: 0.6rem; font-weight: 800; padding: 0.28rem 1rem; letter-spacing: 2.5px; text-transform: uppercase; white-space: nowrap; }
.price-tier { font-family: 'Bebas Neue', sans-serif; font-size: 1rem; letter-spacing: 4px; color: var(--teal); margin-bottom: 0.6rem; }
.price-rule { height: 2px; background: var(--rule); margin: 0.4rem 0 0.9rem; }
.price-amt { font-family: 'Bebas Neue', sans-serif; font-size: 4rem; letter-spacing: 2px; color: var(--ink); line-height: 1; }
.price-amt span { font-family: 'DM Sans', sans-serif; font-size: 0.9rem; color: var(--muted); font-weight: 300; }
.price-desc { font-family: 'DM Sans', sans-serif; font-size: 0.8rem; font-weight: 300; color: var(--muted); margin: 0.5rem 0 1.3rem; line-height: 1.65; font-style: italic; }
.price-list { list-style: none; display: flex; flex-direction: column; gap: 0.65rem; margin-bottom: 1.8rem; }
.price-list li { font-family: 'DM Sans', sans-serif; font-size: 0.82rem; font-weight: 500; color: var(--ink2); display: flex; gap: 0.6rem; line-height: 1.5; }
.price-list li::before { content: '✦'; color: var(--teal); flex-shrink: 0; font-size: 0.65rem; margin-top: 0.15rem; }
.price-list li.off { opacity: 0.28; }
.price-list li.off::before { content: '×'; color: var(--muted); }
.btn-price { width: 100%; padding: 0.82rem; font-family: 'DM Sans', sans-serif; font-weight: 800; font-size: 0.75rem; letter-spacing: 2px; text-transform: uppercase; cursor: pointer; border: 2px solid var(--ink); background: transparent; color: var(--ink); transition: all 0.2s; }
.btn-price:hover { background: var(--ink); color: var(--white); }
.btn-price.fill { background: var(--teal); border-color: var(--teal); color: var(--white); }
.btn-price.fill:hover { background: var(--teal2); border-color: var(--teal2); }

/* ── CTA ── */
.cta { background: var(--darkred); border-top: 3px solid var(--darkred2); padding: 6rem 3rem; text-align: center; position: relative; overflow: hidden; }
.cta::before { content: 'FOOTY ANALYST'; position: absolute; left: 50%; top: 50%; transform: translate(-50%,-50%); font-family: 'Bebas Neue', sans-serif; font-size: 12rem; letter-spacing: 8px; color: rgba(255,255,255,0.04); pointer-events: none; white-space: nowrap; }
.cta-tag { font-family: 'DM Sans', sans-serif; font-size: 0.65rem; font-weight: 800; letter-spacing: 5px; text-transform: uppercase; color: var(--mustard); border: 1px solid rgba(212,148,26,0.4); padding: 0.38rem 1.3rem; display: inline-block; margin-bottom: 1.3rem; }
.cta-h { font-family: 'Bebas Neue', sans-serif; font-size: clamp(3rem, 8vw, 7rem); letter-spacing: 4px; color: var(--white); line-height: 0.92; margin-bottom: 1rem; }
.cta-sub { font-family: 'DM Sans', sans-serif; font-size: 0.9rem; font-weight: 300; color: rgba(255,255,255,0.7); max-width: 420px; margin: 0 auto 2.2rem; line-height: 1.8; font-style: italic; }
.cta-btns { display: flex; gap: 1rem; justify-content: center; flex-wrap: wrap; }

/* ── FOOTER ── */
footer { background: var(--ink); border-top: 3px solid var(--mustard); padding: 2rem 3rem; display: flex; justify-content: space-between; align-items: center; flex-wrap: wrap; gap: 1rem; }
.f-logo { display: flex; align-items: center; gap: 0.6rem; }
.f-badge { width: 26px; height: 26px; background: var(--mustard); border: 1.5px solid var(--white); display: flex; align-items: center; justify-content: center; font-family: 'Bebas Neue', sans-serif; font-size: 0.75rem; letter-spacing: 1px; color: var(--ink); transform: rotate(-3deg); }
.f-name { font-family: 'Bebas Neue', sans-serif; font-size: 1.2rem; letter-spacing: 3px; color: var(--white); }
.f-copy { font-family: 'DM Sans', sans-serif; font-size: 0.68rem; color: rgba(255,255,255,0.35); font-weight: 300; }
.f-links { display: flex; gap: 1.5rem; }
.f-links span { font-family: 'DM Sans', sans-serif; font-size: 0.7rem; font-weight: 500; color: rgba(255,255,255,0.4); cursor: pointer; letter-spacing: 1px; transition: color 0.2s; }
.f-links span:hover { color: var(--mustard); }

/* ── LOGIN ── */
.login-wrap { min-height: 100vh; background: var(--sand3); display: flex; align-items: center; justify-content: center; padding: 2rem; position: relative; }
.login-wrap::before { content: ''; position: absolute; inset: 0; background: repeating-linear-gradient(90deg, transparent, transparent 79px, rgba(26,22,18,0.04) 79px, rgba(26,22,18,0.04) 80px), repeating-linear-gradient(0deg, transparent, transparent 79px, rgba(26,22,18,0.04) 79px, rgba(26,22,18,0.04) 80px); }
.login-card { background: var(--white); border: 2.5px solid var(--ink); padding: 2.8rem; width: 100%; max-width: 400px; position: relative; z-index: 1; box-shadow: 6px 6px 0 var(--ink); }
.login-card::before { content: ''; position: absolute; top: 8px; left: 8px; right: 8px; bottom: 8px; border: 1px solid var(--rule); pointer-events: none; }
.login-logo { display: flex; align-items: center; gap: 0.7rem; justify-content: center; margin-bottom: 0.3rem; }
.login-badge { width: 38px; height: 38px; background: var(--teal); border: 2px solid var(--ink); display: flex; align-items: center; justify-content: center; font-family: 'Bebas Neue', sans-serif; font-size: 1rem; letter-spacing: 1px; color: var(--white); transform: rotate(-3deg); }
.login-name { font-family: 'Bebas Neue', sans-serif; font-size: 1.8rem; letter-spacing: 3px; color: var(--ink); }
.login-sub { font-family: 'DM Sans', sans-serif; font-size: 0.78rem; color: var(--muted); text-align: center; margin-bottom: 1.8rem; font-style: italic; font-weight: 300; }
.role-div { display: flex; align-items: center; gap: 0.8rem; margin-bottom: 1.3rem; }
.role-div-line { flex: 1; height: 1px; background: var(--rule); }
.role-div-t { font-family: 'DM Sans', sans-serif; font-size: 0.6rem; font-weight: 700; letter-spacing: 3px; text-transform: uppercase; color: var(--muted); }
.role-tabs { display: flex; border: 2px solid var(--ink); margin-bottom: 1.5rem; }
.role-tab { flex: 1; padding: 0.58rem; text-align: center; font-family: 'DM Sans', sans-serif; font-size: 0.7rem; letter-spacing: 1.5px; text-transform: uppercase; font-weight: 800; cursor: pointer; transition: all 0.2s; color: var(--muted); border-right: 1px solid var(--rule); }
.role-tab:last-child { border-right: none; }
.role-tab.on { background: var(--teal); color: var(--white); }
.f-label { display: block; font-family: 'DM Sans', sans-serif; font-size: 0.62rem; letter-spacing: 2px; text-transform: uppercase; color: var(--muted); margin-bottom: 0.38rem; font-weight: 700; }
.f-input { width: 100%; background: var(--cream); border: 1.5px solid var(--rule); border-bottom: 2px solid var(--ink); padding: 0.68rem 0.9rem; color: var(--ink); font-family: 'DM Sans', sans-serif; font-size: 0.88rem; font-weight: 500; outline: none; margin-bottom: 1rem; transition: border-color 0.2s; }
.f-input:focus { border-bottom-color: var(--teal); }
.btn-login { width: 100%; background: var(--teal); color: var(--white); border: 2px solid var(--teal); padding: 0.9rem; font-family: 'DM Sans', sans-serif; font-weight: 800; font-size: 0.8rem; letter-spacing: 2.5px; text-transform: uppercase; cursor: pointer; transition: all 0.2s; margin-top: 0.2rem; }
.btn-login:hover { background: var(--ink); border-color: var(--ink); }
.back-link { text-align: center; margin-top: 1.2rem; font-family: 'DM Sans', sans-serif; font-size: 0.75rem; color: var(--muted); cursor: pointer; font-weight: 500; }
.back-link span { color: var(--teal); font-weight: 700; }

/* ── DASHBOARD ── */
.dash { display: grid; grid-template-columns: 220px 1fr; min-height: 100vh; }
.sidebar { background: #1a2422; border-right: 1px solid rgba(255,255,255,0.06); display: flex; flex-direction: column; }
.sb-top { padding: 1.1rem 1.1rem 1rem; border-bottom: 1px solid rgba(255,255,255,0.07); cursor: pointer; display: flex; align-items: center; gap: 0.65rem; }
.sb-badge { width: 30px; height: 30px; background: var(--mustard); border: 2px solid rgba(255,255,255,0.2); display: flex; align-items: center; justify-content: center; font-family: 'Bebas Neue', sans-serif; font-size: 0.85rem; letter-spacing: 1px; color: var(--ink); transform: rotate(-3deg); flex-shrink: 0; }
.sb-title { font-family: 'Bebas Neue', sans-serif; font-size: 1.1rem; letter-spacing: 3px; color: var(--white); }
.sb-subtitle { font-family: 'DM Sans', sans-serif; font-size: 0.45rem; color: var(--mustard); letter-spacing: 2.5px; text-transform: uppercase; font-weight: 500; }

/* Sidebar live counter */
.sb-live { display: flex; align-items: center; gap: 0.5rem; padding: 0.42rem 1.1rem; background: rgba(0,0,0,0.2); border-bottom: 1px solid rgba(255,255,255,0.05); }
.sb-live-dot { width: 6px; height: 6px; border-radius: 50%; background: #4ade80; animation: pulse 2s infinite; flex-shrink: 0; }
.sb-live-text { font-family: 'DM Sans', sans-serif; font-size: 0.58rem; font-weight: 500; color: rgba(255,255,255,0.45); letter-spacing: 1px; flex: 1; }
.sb-live-num { font-family: 'Bebas Neue', sans-serif; font-size: 0.95rem; letter-spacing: 1px; color: #4ade80; }

.sb-nav { padding: 0.5rem 0; flex: 1; overflow-y: auto; }
.sb-nav::-webkit-scrollbar { width: 3px; }
.sb-nav::-webkit-scrollbar-track { background: transparent; }
.sb-nav::-webkit-scrollbar-thumb { background: rgba(255,255,255,0.1); }
.sb-sec { padding: 1rem 1.1rem 0.28rem; font-family: 'DM Sans', sans-serif; font-size: 0.5rem; letter-spacing: 3px; text-transform: uppercase; color: rgba(255,255,255,0.28); font-weight: 800; }
.sb-sec:first-child { padding-top: 0.5rem; }
.sb-item {
  display: flex; align-items: center; gap: 0.65rem;
  padding: 0.58rem 1.1rem; font-family: 'DM Sans', sans-serif;
  font-size: 0.78rem; font-weight: 600; color: rgba(255,255,255,0.52);
  cursor: pointer; transition: all 0.15s; border-left: 3px solid transparent;
  margin: 0.5px 0;
}
.sb-item:hover { color: rgba(255,255,255,0.88); background: rgba(255,255,255,0.05); }
.sb-item.on {
  color: #fff; background: rgba(212,148,26,0.12);
  border-left-color: var(--mustard);
  font-weight: 700;
}
.sb-icon { font-size: 0.82rem; width: 16px; text-align: center; flex-shrink: 0; }
.sb-foot { padding: 0.85rem 1.1rem; border-top: 1px solid rgba(255,255,255,0.07); display: flex; align-items: center; gap: 0.65rem; background: rgba(0,0,0,0.15); }
.sb-av { width: 30px; height: 30px; background: var(--darkred); border: 2px solid var(--mustard); display: flex; align-items: center; justify-content: center; font-family: 'DM Sans', sans-serif; font-size: 0.62rem; font-weight: 800; color: var(--white); flex-shrink: 0; }
.sb-uname { font-family: 'DM Sans', sans-serif; font-size: 0.78rem; font-weight: 700; color: var(--white); }
.sb-role { font-family: 'DM Sans', sans-serif; font-size: 0.58rem; color: var(--mustard); letter-spacing: 2px; text-transform: uppercase; font-weight: 600; }

.main-area { background: var(--cream); overflow-y: auto; }
.topbar {
  display: flex; align-items: center; gap: 0.75rem;
  padding: 0.75rem 1.5rem;
  border-bottom: 1px solid var(--rule);
  background: var(--white);
  position: sticky; top: 0; z-index: 10;
  min-height: 52px;
}
.tb-title {
  font-family: 'Bebas Neue', sans-serif;
  font-size: 1.15rem; letter-spacing: 3px; color: var(--ink); flex: 1;
}
.tb-right { display: flex; gap: 0.45rem; align-items: center; }
.chip { font-family: 'DM Sans', sans-serif; font-size: 0.55rem; font-weight: 800; padding: 0.15rem 0.5rem; letter-spacing: 1.5px; text-transform: uppercase; border: 1.5px solid; border-radius: 1px; }
.chip-teal  { border-color: var(--teal);    color: var(--teal);    background: var(--teal4); }
.chip-red   { border-color: var(--darkred); color: var(--darkred); background: var(--darkred3); }
.chip-must  { border-color: var(--mustard); color: var(--mustard2);background: var(--mustard3); }
.dc { padding: 1.5rem; }

/* ── FM METRICS ── */
.metrics { display: grid; grid-template-columns: repeat(auto-fit, minmax(135px,1fr)); gap: 0.9rem; margin-bottom: 1.2rem; }
.mc { background: var(--white); border: 1.5px solid var(--rule); border-top: 4px solid; padding: 1rem 0.9rem; transition: transform 0.2s; }
.mc:hover { transform: translateY(-2px); }
.mc:nth-child(1) { border-top-color: var(--teal); }
.mc:nth-child(2) { border-top-color: var(--darkred); }
.mc:nth-child(3) { border-top-color: var(--mustard); }
.mc:nth-child(4) { border-top-color: var(--sand); }
.mc-val { font-family: 'Bebas Neue', sans-serif; font-size: 2.2rem; letter-spacing: 2px; color: var(--ink); line-height: 1; }
.mc-lbl { font-family: 'DM Sans', sans-serif; font-size: 0.6rem; font-weight: 700; color: var(--muted); text-transform: uppercase; letter-spacing: 2px; margin-top: 0.28rem; }
.mc-chg { font-family: 'DM Sans', sans-serif; font-size: 0.65rem; font-weight: 600; color: var(--good); margin-top: 0.28rem; }

/* ── PANELS ── */
.g2 { display: grid; grid-template-columns: 1fr 1fr; gap: 1.2rem; margin-top: 1.2rem; }
.panel { background: var(--white); border: 1.5px solid var(--rule); padding: 1.2rem; }
.ph { font-family: 'Bebas Neue', sans-serif; font-size: 1rem; letter-spacing: 2.5px; color: var(--ink); margin-bottom: 1rem; display: flex; align-items: center; justify-content: space-between; border-bottom: 1.5px solid var(--rule); padding-bottom: 0.65rem; }
.ph-chip { font-family: 'DM Sans', sans-serif; font-size: 0.54rem; font-weight: 800; padding: 0.14rem 0.46rem; background: var(--teal); color: var(--white); letter-spacing: 2px; text-transform: uppercase; }

/* ── FM PLAYER ROWS ── */
.pr { display: flex; align-items: center; gap: 0.65rem; padding: 0.52rem 0; border-bottom: 1px solid var(--rule2); }
.pr:last-child { border-bottom: none; }
.pr-n { font-family: 'Bebas Neue', sans-serif; font-size: 0.9rem; color: var(--muted); width: 18px; text-align: center; letter-spacing: 1px; }
.pr-av { width: 27px; height: 27px; background: var(--teal); border: 1.5px solid var(--ink); display: flex; align-items: center; justify-content: center; font-family: 'DM Sans', sans-serif; font-size: 0.58rem; font-weight: 800; color: var(--white); flex-shrink: 0; }
.pr-info { flex: 1; }
.pr-name { font-family: 'DM Sans', sans-serif; font-size: 0.82rem; font-weight: 700; color: var(--ink); }
.pr-meta { font-family: 'DM Sans', sans-serif; font-size: 0.62rem; color: var(--muted); font-weight: 400; }
.pr-bar-bg { width: 72px; background: var(--rule2); height: 5px; position: relative; border: 1px solid var(--rule); }
.pr-bar-fill { background: var(--teal); height: 100%; position: absolute; top: 0; left: 0; }
.pr-sc { font-family: 'Bebas Neue', sans-serif; font-size: 1.1rem; letter-spacing: 1px; color: var(--darkred); width: 26px; text-align: right; }

/* ── FM ATTRIBUTE GRID ── */
.fm-attrs { display: grid; grid-template-columns: repeat(3,1fr); gap: 2px; margin: 0.7rem 0; }
.fma { background: var(--cream2); padding: 0.3rem 0.45rem; display: flex; justify-content: space-between; align-items: center; border: 1px solid var(--rule2); }
.fma-l { font-family: 'DM Sans', sans-serif; font-size: 0.58rem; color: var(--muted); text-transform: uppercase; letter-spacing: 1px; font-weight: 700; }
.fma-v { font-family: 'Bebas Neue', sans-serif; font-size: 0.95rem; letter-spacing: 1px; }
.fv-hi { color: var(--good); } .fv-md { color: var(--warn); } .fv-lo { color: var(--bad); }

/* ── PITCH ── */
.pitch { background: linear-gradient(180deg, #4a7c52 0%, #56895e 35%, #4a7c52 50%, #56895e 65%, #4a7c52 100%); position: relative; overflow: hidden; border: 2px solid var(--rule); }
.pitch svg { position: absolute; inset: 0; width: 100%; height: 100%; }
.pdot { position: absolute; transform: translate(-50%,-50%); cursor: pointer; transition: transform 0.2s; }
.pdot:hover { transform: translate(-50%,-50%) scale(1.25); }
.pdot-inner { width: 23px; height: 23px; border-radius: 50%; display: flex; align-items: center; justify-content: center; font-family: 'DM Sans', sans-serif; font-size: 0.48rem; font-weight: 800; border: 2px solid rgba(255,255,255,0.5); }
.pda .pdot-inner { background: var(--mustard); color: var(--ink); }
.pdr .pdot-inner { background: var(--darkred); color: var(--white); }

/* ── PLAYER CARDS ── */
.pcard { background: var(--white); border: 1.5px solid var(--rule); transition: all 0.2s; cursor: pointer; }
.pcard:hover { border-color: var(--ink); box-shadow: 4px 4px 0 var(--ink); }
.pcard.watched { border-top: 4px solid var(--teal); }
.pcard-top { padding: 0.85rem 0.95rem; border-bottom: 1.5px solid var(--rule); display: flex; align-items: flex-start; justify-content: space-between; background: var(--cream2); }
.pcard-name { font-family: 'DM Sans', sans-serif; font-size: 0.95rem; font-weight: 800; color: var(--ink); }
.pcard-meta { font-family: 'DM Sans', sans-serif; font-size: 0.62rem; color: var(--muted); margin-top: 0.12rem; font-weight: 400; }
.pcard-pos { font-family: 'DM Sans', sans-serif; font-size: 0.56rem; font-weight: 800; padding: 0.14rem 0.45rem; background: var(--teal); color: var(--white); letter-spacing: 2px; text-transform: uppercase; display: inline-block; margin-top: 0.28rem; }
.pcard-ovr { font-family: 'Bebas Neue', sans-serif; font-size: 2.2rem; letter-spacing: 2px; color: var(--darkred); line-height: 1; text-align: right; }
.pcard-ovrl { font-family: 'DM Sans', sans-serif; font-size: 0.52rem; color: var(--muted); letter-spacing: 2px; text-transform: uppercase; text-align: right; font-weight: 600; }
.pcard-body { padding: 0.85rem 0.95rem; }
.pcard-val { font-family: 'DM Sans', sans-serif; font-size: 0.68rem; color: var(--muted); font-style: italic; margin-bottom: 0.38rem; font-weight: 400; }
.pcard-style { font-family: 'DM Sans', sans-serif; font-size: 0.68rem; color: var(--teal); font-style: italic; margin-bottom: 0.45rem; font-weight: 600; }
.pcard-actions { display: flex; gap: 0.4rem; padding: 0.72rem 0.95rem; border-top: 1px solid var(--rule2); }
.pca { flex: 1; padding: 0.4rem; font-family: 'DM Sans', sans-serif; font-size: 0.62rem; font-weight: 800; letter-spacing: 1.5px; text-transform: uppercase; cursor: pointer; border: 1.5px solid var(--rule); color: var(--ink3); background: transparent; transition: all 0.2s; }
.pca:hover { background: var(--ink); color: var(--white); border-color: var(--ink); }
.pca-teal:hover { background: var(--teal); border-color: var(--teal); color: var(--white); }

/* ── VIDEO ── */
.vbox { background: var(--ink2); aspect-ratio: 16/9; display: flex; align-items: center; justify-content: center; position: relative; border: 2px solid var(--rule); }
.vbox-badge { position: absolute; top: 0.65rem; left: 0.65rem; background: var(--darkred); color: var(--white); font-family: 'DM Sans', sans-serif; font-size: 0.58rem; font-weight: 800; padding: 0.16rem 0.55rem; letter-spacing: 2px; text-transform: uppercase; }
.play-btn { width: 52px; height: 52px; border: 3px solid var(--mustard); display: flex; align-items: center; justify-content: center; cursor: pointer; font-size: 1rem; color: var(--mustard); transition: all 0.2s; }
.play-btn:hover { background: var(--mustard); color: var(--ink); }

/* ── PILL ── */
.pill { font-family: 'DM Sans', sans-serif; display: inline-block; font-size: 0.56rem; font-weight: 800; padding: 0.14rem 0.45rem; letter-spacing: 1.5px; text-transform: uppercase; border: 1.5px solid; }
.pill-r { border-color: var(--darkred); color: var(--darkred); background: var(--darkred3); }
.pill-n { border-color: var(--rule); color: var(--muted); }

/* ── MISC ── */
.sep { height: 1.5px; background: var(--rule); margin: 0.95rem 0; }
.full { grid-column: 1/-1; }

@media(max-width: 960px) {
  .dash { grid-template-columns: 1fr; }
  .sidebar {
    position: fixed; top: 0; left: 0; bottom: 0; z-index: 400;
    transform: translateX(-100%); transition: transform 0.25s ease;
    width: 260px; box-shadow: 4px 0 24px rgba(0,0,0,0.35);
  }
  .sidebar.open { transform: translateX(0); }
  .sidebar-overlay {
    display: none; position: fixed; inset: 0; background: rgba(0,0,0,0.5);
    z-index: 399;
  }
  .sidebar-overlay.open { display: block; }
  .topbar { position: sticky; top: 0; z-index: 50; }
  .mobile-menu-btn {
    display: flex; align-items: center; justify-content: center;
    width: 34px; height: 34px; background: transparent;
    border: 1.5px solid rgba(26,22,18,0.2); cursor: pointer;
    flex-direction: column; gap: 4px; padding: 8px; flex-shrink: 0;
  }
  .mobile-menu-btn span {
    display: block; width: 16px; height: 2px;
    background: var(--ink); border-radius: 1px; transition: all 0.2s;
  }
  .g2 { grid-template-columns: 1fr; }
  .feat-grid, .price-grid, .psg-grid, .vt-grid { grid-template-columns: 1fr; }
  .hero-stats { flex-direction: column; }
  .nav-links { display: none; }
  .hero, .sec { padding: 5rem 1.2rem 3rem; }
  .nav { padding: 0 1rem; }
  .dc { padding: 0.85rem; }
  .topbar { padding: 0.6rem 0.85rem; }
  .tb-title { font-size: 1rem; letter-spacing: 2px; }
}
@media(min-width: 961px) {
  .mobile-menu-btn { display: none; }
  .sidebar-overlay { display: none !important; }
}
`;


/* ── DATA for landing page only ── */
const PRO_STYLES = [
  {club:"Manchester City",formation:"4-3-3",manager:"Pep Guardiola",color:"#b3e5fc",desc:"Positional play — control space through constant movement. Players occupy half-spaces and force opponents into narrow positions, then exploit width when the press is broken.",keyPlayers:[{name:"Erling Haaland",role:"Centre-Forward",trait:"Movement & hold-up"},{name:"Kevin De Bruyne",role:"Advanced CM",trait:"Through-ball mastery"},{name:"Phil Foden",role:"Half-space invader",trait:"Tight-space combinations"}],formation:"4-3-3"},
  {club:"Liverpool FC",formation:"4-2-3-1",manager:"Jürgen Klopp",color:"#ffccbc",desc:"High-energy counter-press. Win the ball back within 6 seconds of losing it. Vertical, direct transitions with late runners arriving into the box.",keyPlayers:[{name:"Mohamed Salah",role:"Right Winger",trait:"Cut inside & finish"},{name:"Alexis Mac Allister",role:"Deep CM",trait:"Press resistance"},{name:"Roberto Firmino",role:"Pressing striker",trait:"First-press trigger & link play"}],formation:"4-2-3-1"},
];

/* Simulate live user count — replace with Supabase real-time later */
function useLiveCount(base=3247){
  const [count,setCount]=useState(base);
  useEffect(()=>{
    const t=setInterval(()=>{
      setCount(c=>c+Math.floor(Math.random()*3-1));
    },4000);
    return()=>clearInterval(t);
  },[]);
  return count.toLocaleString();
}

function attrC(v){return v>=80?"fv-hi":v>=65?"fv-md":"fv-lo";}
function ini(n){return n.split(" ").map(w=>w[0]).join("");}
function Orn(){return<div className="hero-ornament"><div className="hero-orn-line"/><span className="hero-orn-sym">✦ ✦ ✦</span><div className="hero-orn-line"/></div>;}

function AppInner(){
  // Secret lab route — accessible at /#/lab without login
  if (window.location.hash === "#/lab" || window.location.pathname === "/lab") {
    return <Lab/>;
  }

  const { user, signOut } = useAuth();
  const [loading, setLoading] = useState(true);
  const [cloudData, setCloudData] = useState(null);
  const [syncActive, setSyncActive] = useState(false);

  useEffect(() => {
    if (!user) { setLoading(false); return; }

    setLoading(true);
    performInitialSync().then(result => {
      setCloudData(result);
      setSyncActive(result.success);
      // Write everything to localStorage cache so app works offline
      if (result.squad?.length > 0) saveToStorage("my_squad", result.squad);
      if (result.reports) saveToStorage("scout_reports", result.reports);
      if (result.drills?.length > 0) saveToStorage("session_drills", result.drills);
      if (result.dossiers?.length > 0) saveToStorage("dossiers", result.dossiers);
      setLoading(false);
    }).catch(() => {
      // On error, still load from localStorage
      setCloudData({
        success: false,
        squad:    loadFromStorage("my_squad", []),
        reports:  loadFromStorage("scout_reports", null),
        drills:   loadFromStorage("session_drills", []),
        dossiers: loadFromStorage("dossiers", []),
      });
      setLoading(false);
    });
  }, [user?.id]);

  if (!user) return <AuthScreen/>;

  if (loading) return (
    <div style={{minHeight:"100vh",background:"var(--teal2)",display:"flex",alignItems:"center",justifyContent:"center",flexDirection:"column",gap:"1.2rem"}}>
      <div style={{fontFamily:"'Bebas Neue',sans-serif",fontSize:"2.2rem",letterSpacing:"5px",color:"var(--white)"}}>Footy Analyst</div>
      <div style={{display:"flex",gap:"0.5rem"}}>
        {[0,1,2].map(i=>(
          <div key={i} style={{width:9,height:9,borderRadius:"50%",background:"var(--mustard)",animation:`bounce 0.8s ease-in-out ${i*0.18}s infinite alternate`}}/>
        ))}
      </div>
      <div style={{fontFamily:"'DM Sans',sans-serif",fontSize:"0.72rem",color:"rgba(255,255,255,0.5)",letterSpacing:"2px",textTransform:"uppercase"}}>
        {syncActive===false ? "Loading your account..." : "Syncing from cloud..."}
      </div>
      <style>{`@keyframes bounce{from{transform:translateY(0)}to{transform:translateY(-10px)}}`}</style>
    </div>
  );

  return <AppDashboard user={user} onSignOut={signOut} cloudData={cloudData} syncActive={syncActive}/>;
}

export default function App(){
  return(
    <ToastProvider>
      <AuthProvider>
        <AppInner/>
      </AuthProvider>
    </ToastProvider>
  );
}

function AppDashboard({user, onSignOut, cloudData=null, syncActive=false}){
  const [view,setView]=useState("dashboard");
  const [tab,setTab]=useState("overview");
  const liveCount=useLiveCount();

  // Shared squad — initialise from cloud data if available, else localStorage
  const [sharedSquad, setSharedSquad] = useState(() => {
    if (cloudData?.squad?.length > 0) return cloudData.squad;
    return loadFromStorage('my_squad', []);
  });
  const [syncStatus] = useState({ syncing: syncActive, reason: syncActive ? "Cloud sync active" : "Local only" });
  const [sidebarOpen, setSidebarOpen] = useState(false);

  // Re-sync squad if cloudData updates after mount
  useEffect(() => {
    if (cloudData?.squad?.length > 0) setSharedSquad(cloudData.squad);
  }, [cloudData?.squad?.length]);

  const handleSquadChange = async (newSquad) => {
    setSharedSquad(newSquad);
    await syncSquadToCloud(newSquad);
  };

  const isScout=user?.role==="scout";
  
  

  const coachNav=[
    {sec:"",          items:[{icon:"📊",label:"Dashboard",        id:"overview"}]},
    {sec:"Match",     items:[{icon:"📅",label:"Fixtures & Results",id:"fixtures"},{icon:"🎯",label:"Opposition Dossier", id:"dossier"},{icon:"⚽",label:"Set Pieces",          id:"setpiece"}]},
    {sec:"Squad",     items:[{icon:"🏟️",label:"Squad & Training",  id:"squadcoach"},{icon:"🎨",label:"Tactics Board",     id:"tacticsboard"},{icon:"🏥",label:"Load Management",   id:"load"}]},
    {sec:"Analysis",  items:[{icon:"📈",label:"Season Tracker",    id:"tracker"},{icon:"📝",label:"Scout Reports",      id:"sreports"},{icon:"📊",label:"Player Comparison",  id:"comparison"},{icon:"🗄️",label:"Player Database",   id:"database"}]},
    {sec:"Coaching",  items:[{icon:"🧠",label:"Tactical Library",  id:"knowledge"},{icon:"✦", label:"AI Analyst",        id:"ai"}]},
  ];
  const scoutNav=[
    {sec:"",          items:[{icon:"📊",label:"Activity",           id:"overview"}]},
    {sec:"Scouting",  items:[{icon:"🗄️",label:"Player Database",   id:"database"},{icon:"⭐",label:"Watchlist",          id:"watchlist"},{icon:"📝",label:"My Reports",         id:"sreports"},{icon:"📊",label:"Player Comparison",  id:"comparison"}]},
    {sec:"Reference", items:[{icon:"🧠",label:"Tactical Library",   id:"knowledge"},{icon:"✦", label:"AI Analyst",        id:"ai"}]},
  ];
  const navSections=isScout?scoutNav:coachNav;
  const LABELS={
    overview:isScout?"Scout Activity":"Dashboard",
    fixtures:"Fixtures & Results", tracker:"Season Tracker",
    load:"Load Management",       profile:"My Profile",
    ai:"✦ AI Analyst",            video:"Match Analysis",
    sreports:"Scout Reports",     knowledge:"Tactical Systems",
    squadcoach:"Squad Builder",   tacticsboard:"Tactics Board",
    dossier:"Opposition Dossier", setpiece:"Set Piece Builder",
    comparison:"Player Comparison",prostyles:"Pro Styles",
    drills:"Drills Library",      database:"Player Database",
    watchlist:"My Watchlist",
  };

  /* ═══ LANDING ═══ */
  if(view==="landing") return(<>
    <style>{CSS}</style>
    <nav className="nav">
      <div className="logo" onClick={()=>setView("landing")}>
        <div className="logo-badge">FA</div>
        <div className="logo-wordmark"><div className="logo-text">Footy Analyst</div><div className="logo-sub">Football Intelligence Platform</div></div>
      </div>
      <div className="nav-links">
        <span className="nav-link" onClick={()=>document.querySelector('.sec-teal')?.scrollIntoView({behavior:'smooth'})}>Features</span>
        <span className="nav-link" onClick={()=>document.querySelector('.sec-sand')?.scrollIntoView({behavior:'smooth'})}>Video</span>
        <span className="nav-link" onClick={()=>document.querySelector('.sec-mustard')?.scrollIntoView({behavior:'smooth'})}>Pro Styles</span>
        <span className="nav-link" onClick={()=>document.querySelector('.sec-red')?.scrollIntoView({behavior:'smooth'})}>Pricing</span>
      </div>
      <div className="nav-btns">
        <div className="live-counter"><div className="live-dot"/><span className="live-text">Live</span><span className="live-num">{liveCount}</span></div>
        <button className="btn-nav-o" onClick={()=>setView("login")}>Log In</button>
        <button className="btn-nav-f" onClick={()=>{setRole("coach");setView("login");}}>Get Started</button>
      </div>
    </nav>

    <section className="hero">
      <div className="hero-corner tl"/><div className="hero-corner tr"/>
      <div className="hero-corner bl"/><div className="hero-corner br"/>
      <div className="hero-eyebrow"><div className="hero-eyebrow-dot"/><span className="hero-eyebrow-text">Est. 2026 · footyanalyst.com</span><div className="hero-eyebrow-dot"/></div>
      <h1 className="hero-h1">The <em>Gaffer's</em><br/>Secret Weapon</h1>
      <Orn/>
      <p className="hero-sub">Upload wide-lens or ball-tracking footage. Study how Guardiola, Ferguson & Wenger built their teams. Apply elite playing styles to develop your own players.</p>
      <div className="hero-btns">
        <button className="btn-hero-p" onClick={()=>{setRole("coach");setView("login");}}>Start as Coach</button>
        <button className="btn-hero-s" onClick={()=>{setRole("scout");setView("login");}}>Start as Scout</button>
      </div>
      <div className="hero-live">
        <div className="hero-live-dot"/>
        <span className="hero-live-text">coaches & scouts active right now</span>
        <div className="hero-live-num">{liveCount}</div>
      </div>
      <div className="hero-stats">
        <div className="hs"><div className="hs-n">50+</div><div className="hs-l">Coachable Moment Rules</div></div>
        <div className="hs"><div className="hs-n">15</div><div className="hs-l">Analysis Engines</div></div>
        <div className="hs"><div className="hs-n">6</div><div className="hs-l">Pro Club Styles</div></div>
        <div className="hs"><div className="hs-n">300+</div><div className="hs-l">Training Drills</div></div>
      </div>
    </section>

    <div className="mq"><div className="mq-inner">{["Manchester City","Liverpool FC","Arsenal FC","Atlético Madrid","Real Madrid","Bayer Leverkusen","Manchester City","Liverpool FC","Arsenal FC","Atlético Madrid","Real Madrid","Bayer Leverkusen"].map((t,i)=><span key={i} className="mq-item">{t} <span className="mq-sep">◆</span></span>)}</div></div>

    <section className="sec sec-teal">
      <div className="sec-label"><div className="sec-line"/><span className="sec-tag">What We Offer</span><div className="sec-line"/></div>
      <h2 className="sec-h" style={{textAlign:"center"}}>Everything the <em>Modern Gaffer</em> Needs</h2>
      <p className="sec-sub" style={{textAlign:"center",margin:"0 auto"}}>From raw video to match-ready tactics — built for coaches who demand professional standards.</p>
      <div className="feat-grid">
        {[{icon:"🎬",title:"AI Video Analysis",desc:"Upload wide-lens, ball-tracking or smartphone footage. Get pressing efficiency scores, shape analysis and key moment tagging within minutes.",n:"01"},{icon:"🏆",title:"Professional Playing Styles",desc:"Study how Guardiola, Wenger & Arteta build their play. Apply their principles with tailored drills matched to your players.",n:"02"},{icon:"🗄️",title:"Player Scouting Database",desc:"FM-style player profiles with full six-attribute grids, video clips, scout reports, watchlists and transfer interest tracking.",n:"03"},{icon:"⚽",title:"Drills Library",desc:"300+ structured training drills modelled on professional methods — from basic possession rondos to advanced positional play.",n:"04"},{icon:"🗺️",title:"Formation Builder",desc:"Drag your squad into any formation. Visualise pressing lines, shape and spacing before your next match.",n:"05"},{icon:"📋",title:"Scout Reports",desc:"File detailed reports with attribute ratings, video clips, notes and recommended interest levels — all in one place.",n:"06"}].map((f,i)=>(
          <div key={i} className="feat-card"><div className="feat-num">{f.n}</div><div className="feat-icon">{f.icon}</div><div className="feat-title">{f.title}</div><div className="feat-desc">{f.desc}</div></div>
        ))}
      </div>
    </section>

    <section className="sec sec-sand">
      <div className="sec-label"><div className="sec-line"/><span className="sec-tag">Video Ingest</span><div className="sec-line"/></div>
      <h2 className="sec-h">What <em>Footage</em> Can We Use?</h2>
      <p className="sec-sub">Built for how real clubs film their football — from grassroots phones to professional ball-tracking systems.</p>
      <div className="vt-grid">
        <div className="vt-card"><div className="vt-icon">📐</div><div className="vt-title">Wide-Lens Tactical Camera</div><div className="vt-desc">The elevated fixed-position camera used by most semi-pro and academy clubs. Captures the full pitch — ideal for shape, pressing structure and formation analysis. Compatible with Hudl, Veo and Pixellot.</div><div className="vt-formats"><span className="vt-fmt">MP4</span><span className="vt-fmt">MOV</span><span className="vt-fmt">AVI</span><span className="vt-fmt">Veo</span><span className="vt-fmt">Hudl</span><span className="vt-fmt">Pixellot</span></div></div>
        <div className="vt-card"><div className="vt-icon">🔵</div><div className="vt-title">Ball-Tracking Camera Feeds</div><div className="vt-desc">Systems like ChyronHego Tracab and Hawk-Eye output positional video alongside tracking data. Gaffer ingests the video directly; raw x/y tracking data imports as JSON or XML for heat maps and pass networks.</div><div className="vt-formats"><span className="vt-fmt">MP4</span><span className="vt-fmt">Tracab</span><span className="vt-fmt">Hawk-Eye</span><span className="vt-fmt">JSON</span></div></div>
        <div className="vt-card"><div className="vt-icon">📱</div><div className="vt-title">Smartphone & Tripod</div><div className="vt-desc">Any elevated smartphone or camcorder recording works for grassroots clubs. No specialist equipment needed — our AI adjusts its analysis model based on camera angle and resolution automatically.</div><div className="vt-formats"><span className="vt-fmt">MP4</span><span className="vt-fmt">MOV</span><span className="vt-fmt">up to 4GB</span></div></div>
        <div className="vt-card"><div className="vt-icon">🎞️</div><div className="vt-title">Training Session Clips</div><div className="vt-desc">Shorter clips from training sessions work brilliantly for drill analysis and player development. Gaffer maps what it sees against your chosen pro playing style to track progress over time.</div><div className="vt-formats"><span className="vt-fmt">Any format</span><span className="vt-fmt">up to 2GB</span></div></div>
      </div>
    </section>

    <section className="sec sec-mustard">
      <div className="sec-label"><div className="sec-line"/><span className="sec-tag">Pro Playing Styles</span><div className="sec-line"/></div>
      <h2 className="sec-h">Learn From the <em>Best Gaffers</em></h2>
      <p className="sec-sub">Study how elite managers set up their teams. Get the key players, principles and drills — then apply them to your squad.</p>
      <div className="psg-grid">
        {PRO_STYLES.slice(0,2).map((s,i)=>(
          <div key={i} className="psg-card">
            <div className="psg-accent" style={{background:s.color}}/>
            <div style={{display:"flex",justifyContent:"space-between",alignItems:"flex-start",marginTop:"0.5rem"}}>
              <div><div className="psg-club">{s.club}</div><div className="psg-form">{s.formation} · {s.manager}</div></div>
              <span className="pill pill-t">Free</span>
            </div>
            <div className="psg-desc">{s.desc}</div>
            <div className="psg-pl-label">Key Players to Study</div>
            {s.keyPlayers.map((p,j)=><div key={j} className="psg-pl-row"><div className="psg-pl-name">{p.name} <span style={{color:"var(--muted)",fontWeight:400,fontStyle:"italic"}}>— {p.role}</span></div><div className="psg-pl-trait">{p.trait}</div></div>)}
            <button className="psg-apply" onClick={()=>{setRole("coach");setView("login");}}>Apply This Style to My Squad →</button>
          </div>
        ))}
      </div>
      <div style={{textAlign:"center",marginTop:"1.8rem"}}>
        <button className="btn-hero-s" style={{color:"var(--ink)",borderColor:"var(--ink)",padding:"0.8rem 2rem",fontSize:"0.78rem"}} onClick={()=>{setRole("coach");setView("login");}}>View All 6 Pro Styles →</button>
      </div>
    </section>

    <section className="sec sec-red">
      <div className="sec-label"><div className="sec-line"/><span className="sec-tag">Pricing</span><div className="sec-line"/></div>
      <h2 className="sec-h" style={{textAlign:"center"}}>Choose Your <em>Plan</em></h2>
      <div className="price-grid">
        <div className="price-card"><div className="price-tier">Basic</div><div className="price-rule"/><div className="price-amt">£19<span>/mo</span></div><div className="price-desc">For grassroots coaches getting started.</div><ul className="price-list"><li>5 video uploads/month</li><li>2 Pro Club Styles (City + Liverpool)</li><li>50+ beginner drills</li><li>Player database (view only)</li><li className="off">Full 6 Pro Styles</li><li className="off">Scout reports & watchlists</li><li className="off">AI video tagging</li></ul><button className="btn-price" onClick={()=>setView("login")}>Get Started</button></div>
        <div className="price-card hot"><div className="price-badge">Most Popular</div><div className="price-tier">Pro</div><div className="price-rule"/><div className="price-amt">£49<span>/mo</span></div><div className="price-desc">For coaches who want professional analysis and full tactical styles.</div><ul className="price-list"><li>25 video uploads/month</li><li>All 6 Pro Club Styles + drills</li><li>Full AI analysis & moment tagging</li><li>150+ drills — all levels</li><li>Full player database (2,000+)</li><li>Scout reports — write & view</li><li>Watchlist & transfer tracking</li></ul><button className="btn-price fill" onClick={()=>setView("login")}>Start Pro Trial</button></div>
        <div className="price-card"><div className="price-tier">Elite</div><div className="price-rule"/><div className="price-amt">£99<span>/mo</span></div><div className="price-desc">The complete platform for semi-pro clubs and academies.</div><ul className="price-list"><li>Unlimited video uploads</li><li>Advanced AI: heatmaps & pass networks</li><li>Ball-tracking data import (JSON/XML)</li><li>All 6 Pro Styles + custom creation</li><li>Full 300+ drill library</li><li>Multi-team management</li><li>Monthly coaching consultation</li></ul><button className="btn-price" onClick={()=>setView("login")}>Go Elite</button></div>
      </div>
    </section>

    <div className="cta">
      <div className="cta-tag">Ready to Elevate Your Game?</div>
      <h2 className="cta-h">Train Like a<br/>Professional</h2>
      <p className="cta-sub">Built for coaches and scouts who want professional analysis tools without the professional price tag.</p>
      <div className="cta-btns">
        <button className="btn-hero-p" onClick={()=>{setRole("coach");setView("login");}}>I'm a Coach</button>
        <button className="btn-hero-s" onClick={()=>{setRole("scout");setView("login");}}>I'm a Scout</button>
      </div>
    </div>
    <footer>
      <div className="f-logo"><div className="f-badge">FA</div><div className="f-name">Footy Analyst</div></div>
      <div className="f-copy">© 2026 Footy Analyst Ltd · footyanalyst.com</div>
      <div className="f-links"><span>Privacy</span><span>Terms</span><span>Contact</span><span>Trademark</span></div>
    </footer>
  </>);

  /* ═══ LOGIN ═══ */
  if(view==="login") return <AuthScreen onSuccess={()=>setView("dashboard")}/>;

  /* ═══ DASHBOARD ═══ */
  return(<>
    <style>{CSS}</style>

    {/* Mobile sidebar overlay */}
    <div className={`sidebar-overlay ${sidebarOpen?"open":""}`} onClick={()=>setSidebarOpen(false)}/>

    <div className="dash">
      <div className={`sidebar ${sidebarOpen?"open":""}`}>
        <div className="sb-top" onClick={()=>setView("landing")}>
          <div className="sb-badge">FA</div>
          <div><div className="sb-title">Footy Analyst</div><div className="sb-subtitle">Football Intelligence</div></div>
        </div>
        <div className="sb-live">
          <div className="sb-live-dot"/>
          <span className="sb-live-text">Active users</span>
          <span className="sb-live-num">{liveCount}</span>
        </div>
        <nav className="sb-nav">
          {navSections.map(sec=>(
            <div key={sec.sec}>
              {sec.sec && <div className="sb-sec">{sec.sec}</div>}
              {sec.items.map(item=>(
                <div key={item.id}
                     className={`sb-item ${tab===item.id?"on":""}`}
                     onClick={()=>{setTab(item.id);setSidebarOpen(false);}}>
                  <span className="sb-icon">{item.icon}</span>
                  {item.label}
                </div>
              ))}
            </div>
          ))}
        </nav>
        <div className="sb-foot" style={{cursor:"pointer"}} onClick={()=>{setTab("profile");setSidebarOpen(false);}}>
          <div className="sb-av" style={{background:loadFromStorage(`profile_${user?.id}`,{})?.kitColour||"var(--darkred)"}}>
            {user?.initials||user?.name?.slice(0,2).toUpperCase()||"FA"}
          </div>
          <div style={{flex:1,minWidth:0}}>
            <div className="sb-uname" style={{overflow:"hidden",textOverflow:"ellipsis",whiteSpace:"nowrap"}}>{user?.name||user?.email}</div>
            <div className="sb-role">{user?.role||"Coach"} · {user?.plan||"Pro"}</div>
          </div>
          <button onClick={e=>{e.stopPropagation();onSignOut();}} style={{background:"transparent",border:"none",color:"rgba(255,255,255,0.4)",cursor:"pointer",fontSize:"0.75rem",padding:"0.2rem"}} title="Sign Out">🚪</button>
        </div>
      </div>

      <div className="main-area">
        <div className="topbar">
          {/* Hamburger — mobile only */}
          <button className="mobile-menu-btn" onClick={()=>setSidebarOpen(o=>!o)} aria-label="Open menu">
            <span/><span/><span/>
          </button>
          <div className="tb-title" style={{flex:1}}>{LABELS[tab]||tab}</div>
          <div className="tb-right">
            <span className={`chip ${isScout?"chip-red":"chip-teal"}`}>{isScout?"🔍 Scout":"🎯 Coach"}</span>
            {syncActive && (
              <span style={{display:"flex",alignItems:"center",gap:"0.28rem",fontSize:"0.58rem",color:"#4ade80",fontWeight:700,letterSpacing:"1px"}}>
                <span style={{width:5,height:5,borderRadius:"50%",background:"#4ade80",display:"inline-block"}}/>Synced
              </span>
            )}
            <UserPill onSignOut={onSignOut} onNavigate={(t)=>{setTab(t);setSidebarOpen(false);}}/>
          </div>
        </div>
        <div className="dc" style={
          ["tacticsboard","setpiece","overview","fixtures","tracker","load","ai","knowledge","drills","prostyles","dossier","squadcoach","comparison","database","watchlist","sreports","video","profile"].includes(tab)
          ? {padding:0}
          : tab==="tacticsboard"||tab==="setpiece" ? {padding:0,overflow:"hidden"} : {}
        }>
          <div style={{animation:"fadeIn 0.15s ease"}}>
            <style>{`@keyframes fadeIn{from{opacity:0;transform:translateY(4px)}to{opacity:1;transform:translateY(0)}}`}</style>

          {tab==="overview"&&<Dashboard isScout={isScout} onNavigate={setTab} user={user}/>}

          {tab==="ai"&&<AIAnalyst squad={sharedSquad} onNavigate={setTab}/>}

          {tab==="fixtures"&&<Fixtures squad={sharedSquad} onNavigate={setTab}/>}

          {tab==="tracker"&&<SeasonTracker squad={sharedSquad} onNavigate={setTab}/>}

          {tab==="load"&&<LoadManagement squad={sharedSquad} onNavigate={setTab}/>}

          {tab==="profile"&&<Profile user={user} onUpdate={u=>saveToStorage('session',u)} onSignOut={onSignOut}/>}

          {tab==="knowledge"&&<CoachingKnowledge onNavigate={setTab}/>}

          {tab==="drills"&&<CoachingKnowledge onNavigate={setTab} initialTab="drills"/>}

          {tab==="prostyles"&&<CoachingKnowledge onNavigate={setTab} initialTab="matcher"/>}

          {tab==="dossier"&&<OppositionDossier squad={sharedSquad} onNavigate={setTab}/>}

          {tab==="setpiece"&&<SetPieceBuilder squad={sharedSquad}/>}

          {tab==="comparison"&&<PlayerComparison squad={sharedSquad} onNavigate={setTab}/>}

          {tab==="squadcoach"&&<SquadCoach externalPlayers={[]} sharedSquad={sharedSquad} onSquadChange={handleSquadChange} onNavigate={setTab}/>}

          {tab==="tacticsboard"&&<TacticsBoard squadPlayers={sharedSquad} onNavigateToSquad={()=>setTab("squadcoach")}/>}

          {tab==="video"&&<VideoAnalysis/>}

          {tab==="sreports"&&<ScoutReports isScout={isScout} initialReports={cloudData?.reports}/>}

          {(tab==="database"||tab==="watchlist")&&(
            <PlayerDatabase isScout={isScout} watchlistOnly={tab==="watchlist"}/>
          )}

          </div>
        </div>
      </div>
    </div>
  </>);
}
